/**
 * Maps React Router path patterns to i18n keys for document metadata.
 * Keys reference `meta.vendor.pages.*` in the shared i18n translations.
 */
export interface PageMetaEntry {
  titleKey: string;
  descriptionKey: string;
}

export const PAGE_META_MAP: Array<{ pattern: string; meta: PageMetaEntry }> = [
  // Auth
  {
    pattern: "/login",
    meta: {
      titleKey: "meta.vendor.pages.login.title",
      descriptionKey: "meta.vendor.pages.login.description",
    },
  },
  {
    pattern: "/register",
    meta: {
      titleKey: "meta.vendor.pages.register.title",
      descriptionKey: "meta.vendor.pages.register.description",
    },
  },
  {
    pattern: "/verify",
    meta: {
      titleKey: "meta.vendor.pages.verify.title",
      descriptionKey: "meta.vendor.pages.verify.description",
    },
  },
  {
    pattern: "/forgot-password",
    meta: {
      titleKey: "meta.vendor.pages.forgotPassword.title",
      descriptionKey: "meta.vendor.pages.forgotPassword.description",
    },
  },

  // Onboarding (match before generic /onboard)
  {
    pattern: "/onboard/company",
    meta: {
      titleKey: "meta.vendor.pages.onboardCompany.title",
      descriptionKey: "meta.vendor.pages.onboardCompany.description",
    },
  },
  {
    pattern: "/onboard/contact",
    meta: {
      titleKey: "meta.vendor.pages.onboardContact.title",
      descriptionKey: "meta.vendor.pages.onboardContact.description",
    },
  },
  {
    pattern: "/onboard/about",
    meta: {
      titleKey: "meta.vendor.pages.onboardAbout.title",
      descriptionKey: "meta.vendor.pages.onboardAbout.description",
    },
  },
  {
    pattern: "/onboard/review",
    meta: {
      titleKey: "meta.vendor.pages.onboardReview.title",
      descriptionKey: "meta.vendor.pages.onboardReview.description",
    },
  },
  {
    pattern: "/onboard/pending",
    meta: {
      titleKey: "meta.vendor.pages.onboardPending.title",
      descriptionKey: "meta.vendor.pages.onboardPending.description",
    },
  },
  {
    pattern: "/onboard/documents",
    meta: {
      titleKey: "meta.vendor.pages.onboardDocuments.title",
      descriptionKey: "meta.vendor.pages.onboardDocuments.description",
    },
  },
  {
    pattern: "/onboard/services/:vendorServiceId",
    meta: {
      titleKey: "meta.vendor.pages.serviceDetail.title",
      descriptionKey: "meta.vendor.pages.serviceDetail.description",
    },
  },
  {
    pattern: "/onboard/services",
    meta: {
      titleKey: "meta.vendor.pages.services.title",
      descriptionKey: "meta.vendor.pages.services.description",
    },
  },
  {
    pattern: "/onboard",
    meta: {
      titleKey: "meta.vendor.pages.onboardWelcome.title",
      descriptionKey: "meta.vendor.pages.onboardWelcome.description",
    },
  },

  // Status screens
  {
    pattern: "/status/suspended",
    meta: {
      titleKey: "meta.vendor.pages.statusSuspended.title",
      descriptionKey: "meta.vendor.pages.statusSuspended.description",
    },
  },
  {
    pattern: "/status/rejected",
    meta: {
      titleKey: "meta.vendor.pages.statusRejected.title",
      descriptionKey: "meta.vendor.pages.statusRejected.description",
    },
  },

  // Portal pages
  {
    pattern: "/services/:vendorServiceId",
    meta: {
      titleKey: "meta.vendor.pages.serviceDetail.title",
      descriptionKey: "meta.vendor.pages.serviceDetail.description",
    },
  },
  {
    pattern: "/services",
    meta: {
      titleKey: "meta.vendor.pages.services.title",
      descriptionKey: "meta.vendor.pages.services.description",
    },
  },
  {
    pattern: "/orders",
    meta: {
      titleKey: "meta.vendor.pages.orders.title",
      descriptionKey: "meta.vendor.pages.orders.description",
    },
  },
  {
    pattern: "/work-orders/:workOrderId",
    meta: {
      titleKey: "meta.vendor.pages.workOrderDetail.title",
      descriptionKey: "meta.vendor.pages.workOrderDetail.description",
    },
  },
  {
    pattern: "/work-orders",
    meta: {
      titleKey: "meta.vendor.pages.workOrders.title",
      descriptionKey: "meta.vendor.pages.workOrders.description",
    },
  },
  {
    pattern: "/bidding/:requestId",
    meta: {
      titleKey: "meta.vendor.pages.biddingDetail.title",
      descriptionKey: "meta.vendor.pages.biddingDetail.description",
    },
  },
  {
    pattern: "/bidding",
    meta: {
      titleKey: "meta.vendor.pages.bidding.title",
      descriptionKey: "meta.vendor.pages.bidding.description",
    },
  },
  {
    pattern: "/bids",
    meta: {
      titleKey: "meta.vendor.pages.myBids.title",
      descriptionKey: "meta.vendor.pages.myBids.description",
    },
  },
  {
    pattern: "/approvals",
    meta: {
      titleKey: "meta.vendor.pages.approvals.title",
      descriptionKey: "meta.vendor.pages.approvals.description",
    },
  },
  {
    pattern: "/coverage",
    meta: {
      titleKey: "meta.vendor.pages.coverage.title",
      descriptionKey: "meta.vendor.pages.coverage.description",
    },
  },
  {
    pattern: "/fleet",
    meta: {
      titleKey: "meta.vendor.pages.assets.title",
      descriptionKey: "meta.vendor.pages.assets.description",
    },
  },
  {
    pattern: "/documents",
    meta: {
      titleKey: "meta.vendor.pages.documents.title",
      descriptionKey: "meta.vendor.pages.documents.description",
    },
  },
  {
    pattern: "/pricing",
    meta: {
      titleKey: "meta.vendor.pages.pricing.title",
      descriptionKey: "meta.vendor.pages.pricing.description",
    },
  },
  {
    pattern: "/profile",
    meta: {
      titleKey: "meta.vendor.pages.profile.title",
      descriptionKey: "meta.vendor.pages.profile.description",
    },
  },
  {
    pattern: "/",
    meta: {
      titleKey: "meta.vendor.pages.dashboard.title",
      descriptionKey: "meta.vendor.pages.dashboard.description",
    },
  },
];

export const SITE_NAME_KEY = "meta.vendor.siteName";
export const DEFAULT_DESCRIPTION_KEY = "meta.vendor.defaultDescription";
